import { Component, Input, Output, EventEmitter } from '@angular/core';

import { MenuProduct } from 'src/app/shared/model/MenuProduct';
import { Option } from 'src/app/shared/model/Option';
import { Extra } from 'src/app/shared/model/Extra';
import { PizzaDTO } from 'src/app/shared/model/dto/PizzaDTO';
import { FractionPipe } from 'src/app/shared/pipe/fraction.pipe';

export interface FooterListItem {
	prefix?: string;
	name: string;
	qty?: number;
	price?: number;
	total?: number;
	indent?: boolean;
	showPlus?: boolean;
}

@Component({
	selector: 'app-complements-footer',
	templateUrl: './complements-footer.component.html',
	styleUrls: ['./complements-footer.component.scss'],
})
export class ComplementsFooterComponent {
	@Input() item!: MenuProduct;
	@Input() size?: PizzaDTO;
	@Input() extras?: Extra[];
	@Input() selectedExtras: (Option | FooterListItem)[] = [];
	@Input() totalPrice: number = 0;
	@Input() stepPrice: number = 0;
	@Input() buttonLabel: string = '';
	@Input() disabled: boolean = false;
	@Input() showCounter: boolean = false;
	@Input() showCartIcon: boolean = false;
	@Input() showTotalPrice: boolean = false;
	@Input() itemQty: number = 1;

	@Output() action = new EventEmitter<void>();
	@Output() blockedAction = new EventEmitter<void>();
	@Output() increment = new EventEmitter<void>();
	@Output() decrement = new EventEmitter<void>();

	constructor(private fractionPipe: FractionPipe) { }

	onAddClick() {
		if (this.disabled) {
			this.blockedAction.emit();
		} else {
			this.action.emit();
		}
	}

	get displayPrice(): number {
		if (this.showTotalPrice) {
			if (this.size) {
				return this.size.totalPrice || 0;
			}
			return this.totalPrice;
		}
		return this.stepPrice;
	}

	get displayItems(): (Option | FooterListItem)[] {
		if (this.size) return this.buildItemsFromSize(this.size);
		if (this.extras) return this.buildItemsFromExtras(this.extras);
		return this.selectedExtras;
	}

	private buildItemsFromSize(size: PizzaDTO): FooterListItem[] {
		const items: FooterListItem[] = [];

		size.selectedFlavors.forEach(flavor => {
			const slicePrice = flavor.slicePrice ?? flavor.price ?? 0;
			const isIncremental = size.priceBehavior === 'incremental';
			const prefix = this.fractionPipe.transform(flavor.quantity, size.qttyOfFlavorsSelected);
			items.push({
				prefix,
				name: flavor.name,
				price: isIncremental ? slicePrice * flavor.quantity : slicePrice,
				total: isIncremental ? slicePrice * flavor.quantity : slicePrice,
				showPlus: isIncremental,
			});

			(flavor.additionalToppings ?? []).forEach(topping => {
				items.push({
					name: `+ ${topping.name}`,
					price: topping.price,
					total: topping.price,
					indent: true,
				});
			});
		});

		if (size.crust?.name) {
			items.push({
				prefix: 'Borda:',
				name: size.crust.name,
				price: size.crust.price,
				total: size.crust.price,
				showPlus: true,
			});
		}

		if (size.dough?.name) {
			items.push({
				prefix: 'Massa:',
				name: size.dough.name,
				price: size.dough.price,
				total: size.dough.price,
				showPlus: true,
			});
		}

		return items;
	}

	private buildItemsFromExtras(extras: Extra[]): FooterListItem[] {
		const items: FooterListItem[] = [];

		extras.forEach(extra => {
			extra.options.forEach(option => {
				if (extra.type === 1 && option.id === extra.radioGroup) {
					items.push({ name: option.name, price: option.price, total: option.price });
				} else if ((extra.type === 2 || extra.type === 3) && option.qty > 0) {
					items.push({ name: option.name, qty: option.qty, price: option.price, total: option.qty * option.price });
				}
			});
		});

		return items;
	}
}
